// A tiny synchronous Seal highlighter for SEARCH RESULTS (not the editor, which
// uses Monaco). Given a single line, it produces class-tagged segments, then
// overlays a match range so the search snippet shows both syntax colour and the
// matched substring. Fast enough to run per result line; it scans one line at a
// time and never pulls Monaco.

import { KEYWORDS, TYPES, BUILTINS, LITERALS } from "./sealWords";

const KW = new Set(KEYWORDS);
const TY = new Set(TYPES);
const BI = new Set(BUILTINS);
const LI = new Set(LITERALS);

export interface HlSeg {
  value: string;
  /** Token class (drives the .bsh-<cls> colour), or "" for default text. */
  cls: string;
  /** True where this segment falls inside the search match range. */
  matched: boolean;
}

function classify(word: string): string {
  if (KW.has(word)) return "keyword";
  if (TY.has(word)) return "type";
  if (BI.has(word)) return "builtin";
  if (LI.has(word)) return "literal";
  if (/^[A-Z]/.test(word)) return "type"; // PascalCase reads as a type
  return "";
}

const NUM = /^(0[xX][0-9a-fA-F_]+|\d[\d_]*\.\d[\d_]*|\d[\d_]*)/;
const WORD = /^[A-Za-z_]\w*/;
const ANNOT = /^@[A-Za-z_]\w*/;

/** Tokenize one line into class-tagged spans with full character coverage. */
function tokenize(line: string): { value: string; cls: string }[] {
  const out: { value: string; cls: string }[] = [];
  let buf = "";
  const flush = () => {
    if (buf) {
      out.push({ value: buf, cls: "" });
      buf = "";
    }
  };
  let i = 0;
  while (i < line.length) {
    const rest = line.slice(i);
    if (rest.startsWith("//")) {
      flush();
      out.push({ value: rest, cls: "comment" });
      break;
    }
    if (line[i] === '"') {
      flush();
      let j = i + 1;
      while (j < line.length && line[j] !== '"') {
        if (line[j] === "\\") j++;
        j++;
      }
      j = Math.min(j + 1, line.length);
      out.push({ value: line.slice(i, j), cls: "string" });
      i = j;
      continue;
    }
    const annot = ANNOT.exec(rest);
    if (annot) {
      flush();
      out.push({ value: annot[0], cls: "annotation" });
      i += annot[0].length;
      continue;
    }
    const prev = line[i - 1] ?? " ";
    const num = NUM.exec(rest);
    if (num && !/\w/.test(prev)) {
      flush();
      out.push({ value: num[0], cls: "number" });
      i += num[0].length;
      continue;
    }
    const w = WORD.exec(rest);
    if (w) {
      flush();
      out.push({ value: w[0], cls: classify(w[0]) });
      i += w[0].length;
      continue;
    }
    buf += line[i];
    i++;
  }
  flush();
  return out;
}

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

/** Highlight `line`, splitting tokens at the match range [mStart, mEnd) so the
 *  matched characters can be background-marked while keeping their colour. */
export function highlight(line: string, mStart: number, mEnd: number): HlSeg[] {
  const toks = tokenize(line);
  const segs: HlSeg[] = [];
  let pos = 0;
  const hasMatch = mEnd > mStart;
  for (const t of toks) {
    const s = pos;
    const e = pos + t.value.length;
    const cuts = [...new Set([s, clamp(mStart, s, e), clamp(mEnd, s, e), e])].sort((a, b) => a - b);
    for (let k = 0; k < cuts.length - 1; k++) {
      const a = cuts[k];
      const b = cuts[k + 1];
      if (a >= b) continue;
      segs.push({
        value: line.slice(a, b),
        cls: t.cls,
        matched: hasMatch && a >= mStart && b <= mEnd,
      });
    }
    pos = e;
  }
  return segs;
}
